// Transcription tidy-up — the preprocessing pass for AI-transcribed scores.
//
// An audio-to-score transcription (muscriptor et al., arriving as MusicXML)
// is pitch- and grid-accurate but conventionally wrong in ways a human
// engraver never is, and each wrongness is MEASURABLE from the score's own
// statistics rather than guessed:
//
//   1. KEY — percussion-first exports carried no signature and per-part keys
//      disagree (the George's Dilemma probe: piano 3♯, horns written in ♭5,
//      music in C♯ minor). `detectKey` is a duration-weighted
//      Krumhansl–Schmuckler match over all 24 keys, bass register doubled
//      because roots ground tonality.
//   2. BARRING — beat tracking is right, bar GROUPING wrong (the classic AMT
//      metrical-level error: 54 bars of 3/4 over what Jazzomat's reference
//      transcription confirms is a 4/4 form). The decider is HARMONIC
//      RHYTHM: chord changes concentrate on downbeats of the true bar
//      length, so each candidate is scored by how much of the score's
//      chroma novelty its best phase captures. Attack-position histograms
//      were tried first and are near-uniform for busy accompaniments (a
//      tumbao fills every eighth slot of its cell; a walking line fills
//      every beat), so attacks now only GATE the decision (enough material,
//      on a clean eighth grid) rather than make it. Measured on the real
//      file the novelty ratio is 1.41 against the written meter while the
//      openbook 151's worst false signal is 1.16 — the 1.25 margin sits
//      between, and the corpus sweep pins zero suggestions on human-written
//      scores, waltzes and Take Five included (the written bar length always
//      competes as its own candidate, which is what protects 5/4). Because
//      the grid itself is exact, re-barring is then a mechanical re-pour,
//      not a re-hearing.
//   3. SPELLING — transposing parts written on the wrong enharmonic side land
//      concert F♭/C♭/E𝄫. `respellToKey` re-spells every head through the
//      app's own key-preference speller (`spellMidi` with a key context).
//   4. RHYTHM DUST — attack lengths quantized to a 128th grid arrive as tied
//      64th/128th tails. Quantizing attacks and releases to a 16th grid (the
//      default; callers pick) trades ≤1/64-note timing for readability.
//
// Re-barring and quantizing share ONE re-pour: parts flatten to tie-merged
// absolute-time attacks, then pour back into bar spans through the same
// `slotsToItems` every other content path uses, so triplet grouping and tied
// splitting cannot diverge from the editors. The pour REFUSES (with a
// reported reason, never silently) anything it cannot re-pour faithfully:
// structured forms (repeats/endings/navigation — re-grouping bars under a
// volta is not a mechanical operation), tuplet groups (a bracket split at a
// new barline has no written form) and grace notes. Key detection and
// respelling still run on refused scores — they touch no timing.
//
// Everything here is pure: callers commit the returned Score (the store
// action snapshots for undo like any other score edit).

import { uid } from '../song';
import type { KeyContext } from '../notes';
import { detectHarmony, detectPartRoles } from './detectHarmony';
import { slotsToItems, type WrittenSlot } from './convert';
import {
  addF,
  compareF,
  equalsF,
  frac,
  fractionToNumber,
  measureQuarters,
  mulF,
  type Fraction,
} from './duration';
import { midiOf, spellMidi, type SpelledPitch } from './pitch';
import { voiceTiming } from './timing';
import type {
  FormMeasure,
  HarmonySymbol,
  KeySignature,
  NoteEvent,
  NoteHead,
  Part,
  PartRole,
  Score,
  Voice,
  VoiceItem,
} from './types';

const subF = (a: Fraction, b: Fraction): Fraction => addF(a, mulF(b, frac(-1)));

// ---------------------------------------------------------------------------
// Shared extraction
// ---------------------------------------------------------------------------

/** Absolute [start, end) span of every form measure, honouring per-bar meter
 *  changes and a shortened pickup bar. Quarters, exact. */
export function formSpans(form: FormMeasure[]): { start: Fraction; end: Fraction }[] {
  let timeSig: [number, number] = form[0]?.timeSig ?? [4, 4];
  let cursor = frac(0);
  return form.map((m) => {
    timeSig = m.timeSig ?? timeSig;
    const length = m.pickupBeats
      ? mulF(frac(Math.round(m.pickupBeats * 96), 96), frac(4, timeSig[1]))
      : measureQuarters(timeSig);
    const span = { start: cursor, end: addF(cursor, length) };
    cursor = span.end;
    return span;
  });
}

interface AttackSample {
  /** Absolute onset in quarters (float — analysis only). */
  start: number;
  /** Lowest sounding pitch of the attack. */
  minMidi: number;
  /** Written duration of the attacking event, quarters (first tied piece). */
  durQ: number;
}

/** Every sounding ATTACK in the score (tie continuations and graces are not
 *  attacks), in float time — the light extraction the detectors read. Total:
 *  works on any score, tuplets and all. */
function attackSamples(score: Score): AttackSample[] {
  const out: AttackSample[] = [];
  for (const part of score.parts) {
    const spans = formSpans(score.form);
    part.measures.forEach((pm, mi) => {
      const barStart = spans[mi] ? fractionToNumber(spans[mi].start) : 0;
      for (const voice of pm.voices) {
        for (const timed of voiceTiming(voice)) {
          const ev = timed.event;
          if (ev.kind !== 'note' || ev.grace) continue;
          const fresh = ev.notes.filter((h) => h.tie !== 'stop' && h.tie !== 'both');
          if (fresh.length === 0) continue;
          out.push({
            start: barStart + fractionToNumber(timed.start),
            minMidi: Math.min(...fresh.map((h) => midiOf(h.pitch))),
            durQ: fractionToNumber(timed.duration),
          });
        }
      }
    });
  }
  return out.sort((a, b) => a.start - b.start);
}

// ---------------------------------------------------------------------------
// Key detection (duration-weighted Krumhansl–Schmuckler)
// ---------------------------------------------------------------------------

// The classic probe-tone profiles (Krumhansl 1990) — how strongly each scale
// degree asserts a major/minor tonic. Published constants, quoted verbatim.
const KS_MAJOR = [6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88];
const KS_MINOR = [6.33, 2.68, 3.52, 5.38, 2.6, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17];

// Conventional tonic names per pitch class (the fifths tables' −5..6 range).
const MAJOR_TONIC = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B'];
const MINOR_TONIC = ['C', 'C#', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'G#', 'A', 'Bb', 'B'];

function correlation(a: number[], b: number[]): number {
  const n = a.length;
  const ma = a.reduce((s, x) => s + x, 0) / n;
  const mb = b.reduce((s, x) => s + x, 0) / n;
  let num = 0;
  let da = 0;
  let db = 0;
  for (let i = 0; i < n; i++) {
    num += (a[i] - ma) * (b[i] - mb);
    da += (a[i] - ma) ** 2;
    db += (b[i] - mb) ** 2;
  }
  return da === 0 || db === 0 ? 0 : num / Math.sqrt(da * db);
}

/** Detect the key from the score's own pitch content: pitch classes weighted
 *  by sounding duration (bass register doubled — roots ground tonality),
 *  matched against the 24 Krumhansl–Schmuckler profiles. Null when the score
 *  has no sounding notes. */
export function detectKey(score: Score): KeySignature | null {
  const weights = new Array<number>(12).fill(0);
  let any = false;
  const spans = formSpans(score.form);
  for (const part of score.parts) {
    part.measures.forEach((pm, mi) => {
      if (!spans[mi]) return;
      for (const voice of pm.voices) {
        for (const timed of voiceTiming(voice)) {
          const ev = timed.event;
          if (ev.kind !== 'note' || ev.grace) continue;
          for (const head of ev.notes) {
            const midi = midiOf(head.pitch);
            const w = fractionToNumber(timed.duration) * (midi < 50 ? 2 : 1);
            weights[((midi % 12) + 12) % 12] += w;
            any = true;
          }
        }
      }
    });
  }
  if (!any) return null;
  let best: { r: number; pc: number; mode: 'major' | 'minor' } | null = null;
  for (let pc = 0; pc < 12; pc++) {
    const rotated = weights.map((_, i) => weights[(i + pc) % 12]);
    for (const mode of ['major', 'minor'] as const) {
      const r = correlation(rotated, mode === 'major' ? KS_MAJOR : KS_MINOR);
      if (!best || r > best.r) best = { r, pc, mode };
    }
  }
  return best
    ? {
        tonic: (best.mode === 'major' ? MAJOR_TONIC : MINOR_TONIC)[best.pc],
        mode: best.mode,
      }
    : null;
}

// ---------------------------------------------------------------------------
// Bar-structure detection
// ---------------------------------------------------------------------------

export interface BarStructure {
  /** True when the evidence says the written barring is wrong AND decisive. */
  rebar: boolean;
  /** Why no re-bar is suggested (present exactly when rebar is false). */
  reason?: string;
  /** Proposed bar length in quarters (a 2-quarter cell groups as common time). */
  barQ?: number;
  /** Proposed downbeat offset from the score start, quarters. */
  phaseQ?: number;
  /** Novelty-lift ratio of the winner over the best written-consistent
   *  candidate (≥ REBAR_MARGIN when rebar is true). */
  confidence?: number;
}

const EIGHTH_EPS = 1e-6;
const onEighthGrid = (t: number) => Math.abs(t * 2 - Math.round(t * 2)) < EIGHTH_EPS;

/** Duration-weighted chroma per eighth window, bass register doubled (the
 *  harmony a rhythm section states lives in its low voicings and roots). */
function chromaByEighth(score: Score): Float64Array[] {
  const spans = formSpans(score.form);
  const total = spans.length ? fractionToNumber(spans[spans.length - 1].end) : 0;
  const chroma = Array.from({ length: Math.ceil(total * 2) }, () => new Float64Array(12));
  for (const part of score.parts) {
    part.measures.forEach((pm, mi) => {
      if (!spans[mi]) return;
      const barStart = fractionToNumber(spans[mi].start);
      for (const voice of pm.voices) {
        for (const timed of voiceTiming(voice)) {
          const ev = timed.event;
          if (ev.kind !== 'note' || ev.grace) continue;
          const s = barStart + fractionToNumber(timed.start);
          const e = s + fractionToNumber(timed.duration);
          for (let w = Math.max(0, Math.floor(s * 2)); w < Math.min(chroma.length, e * 2); w++) {
            const overlap = Math.min(e, (w + 1) / 2) - Math.max(s, w / 2);
            if (overlap <= 0) continue;
            for (const head of ev.notes) {
              const midi = midiOf(head.pitch);
              chroma[w][((midi % 12) + 12) % 12] += overlap * (midi < 50 ? 2 : 1);
            }
          }
        }
      }
    });
  }
  return chroma;
}

/** Cosine novelty of the chroma at each eighth boundary, summed over 2-quarter
 *  windows either side — the harmonic-rhythm signal. A vamp restating its
 *  root after its fifth would read as constant novelty eighth-to-eighth, but
 *  its 2-quarter chroma is steady; only a real chord change moves it. The
 *  window is fixed (not bar-relative) so candidates compare fairly. */
const NOVELTY_WINDOW_EIGHTHS = 4;

export function chromaNovelty(score: Score): number[] {
  const chroma = chromaByEighth(score);
  const novelty = new Array<number>(chroma.length).fill(0);
  for (let w = 1; w < chroma.length; w++) {
    const a = new Float64Array(12);
    const b = new Float64Array(12);
    for (let k = Math.max(0, w - NOVELTY_WINDOW_EIGHTHS); k < w; k++) {
      for (let pc = 0; pc < 12; pc++) a[pc] += chroma[k][pc];
    }
    for (let k = w; k < Math.min(chroma.length, w + NOVELTY_WINDOW_EIGHTHS); k++) {
      for (let pc = 0; pc < 12; pc++) b[pc] += chroma[k][pc];
    }
    let dot = 0;
    let na = 0;
    let nb = 0;
    for (let pc = 0; pc < 12; pc++) {
      dot += a[pc] * b[pc];
      na += a[pc] * a[pc];
      nb += b[pc] * b[pc];
    }
    if (na === 0 && nb === 0) continue;
    novelty[w] = na === 0 || nb === 0 ? 1 : 1 - dot / Math.sqrt(na * nb);
  }
  return novelty;
}

/** Share of the score's total novelty that bar length `barQ`'s best downbeat
 *  phase captures, normalised so uniform = 1 (share × slot count). */
export function noveltyLift(novelty: number[], barQ: number): { lift: number; phaseQ: number } {
  const slots = Math.round(barQ * 2);
  const total = novelty.reduce((s, x) => s + x, 0);
  if (total < 1e-9) return { lift: 0, phaseQ: 0 };
  let best = 0;
  let phaseQ = 0;
  for (let phi = 0; phi < slots; phi++) {
    let sum = 0;
    for (let w = phi; w < novelty.length; w += slots) sum += novelty[w];
    if (sum > best + 1e-9) {
      best = sum;
      phaseQ = phi / 2;
    }
  }
  return { lift: (best / total) * slots, phaseQ };
}

/** How much stronger the winning bar length must score than the best
 *  written-consistent one before a re-bar is proposed. Calibrated: the
 *  muscriptor George's Dilemma (independently confirmed 4/4, written 3/4)
 *  scores 1.41; the openbook 151's worst false signal is 1.16 (My Favorite
 *  Things); the corpus sweep pins the zero. */
const REBAR_MARGIN = 1.25;
const MIN_ATTACKS = 24;
const MIN_GRID_SHARE = 0.8;
/** Enough long melodic notes for arrival evidence to decide the phase. */
const MIN_ARRIVALS = 8;
/** Novelty's residual say once arrivals decide — a tie-break, small enough
 *  never to flip a real arrival margin (audit: arrival gap 0.17, novelty
 *  gap 0.38 × 0.15 = 0.06). */
const ARRIVAL_NOVELTY_TIEBREAK = 0.15;

/** A long note's evidential weight at offset `delta` into a candidate bar:
 *  phrases ARRIVE on the one, or push INTO it from beat 4 / the and of 4 —
 *  both common, so the push rung is nearly as strong, and the decision is
 *  carried by the anti-evidence: a long note beginning on beat 2 is not a
 *  phrase shape this music has. Mid-bar (beat 3) arrivals are real but
 *  weak. Calibrated on the George's Dilemma audio audit (the horns' 38
 *  arrivals score the true phase 0.55 vs the anticipation phase's 0.38)
 *  against the dwelling-bass fixture (all arrivals on its one: true phase
 *  1.0 vs 0.75 for the one-late reading the push rung would otherwise
 *  reward). */
function arrivalWeight(delta: number, barQ: number): number {
  const eps = 0.01;
  if (delta < eps || delta > barQ - eps) return 1.0; // on the one
  if (delta >= barQ - 1 - eps) return 0.75; // pushed from 4 / and-of-4
  if (delta >= 1 - eps && delta <= 1.5 + eps) return -1.0; // "arrival on 2"
  if (barQ >= 4 && Math.abs(delta - barQ / 2) <= 0.01) return 0.15; // half-bar
  return 0;
}

/** Long (tie-merged, ≥1.5q) non-bass attacks from MELODIC parts — the
 *  arrivals whose placement an anticipated groove cannot fake. Comp parts
 *  are excluded (their held chords push WITH the anticipated bass, and comp
 *  obeys no phrase-shape rules — on the audit file the piano's 133 pushed
 *  chords outvoted the horns' 38 honest arrivals until they were). */
function longArrivals(score: Score): { start: number }[] {
  const detected = detectPartRoles(score);
  const spans = formSpans(score.form);
  const out: { start: number }[] = [];
  for (const part of score.parts) {
    const role = part.role ?? detected.get(part.id);
    if (role === 'comp' || role === 'bass') continue;
    for (const lane of flattenLanes(part, spans)) {
      for (const attack of lane) {
        if (attack.heads.some((h) => midiOf(h.pitch) < 50)) continue;
        const start = fractionToNumber(attack.start);
        const end = Math.max(...attack.heads.map((h) => fractionToNumber(h.end)));
        if (end - start >= 1.5) out.push({ start });
      }
    }
  }
  return out;
}

/** Decide whether the written bars group the beat grid wrongly. Evidence-
 *  gated: anything short of decisive returns rebar:false with the reason. */
export function detectBarStructure(score: Score): BarStructure {
  const meters = new Set<string>();
  let timeSig: [number, number] = score.form[0]?.timeSig ?? [4, 4];
  for (const m of score.form) {
    timeSig = m.timeSig ?? timeSig;
    meters.add(timeSig.join('/'));
  }
  if (meters.size > 1) return { rebar: false, reason: 'the form mixes metres' };
  if (score.form[0]?.pickupBeats) return { rebar: false, reason: 'the form has a pickup bar' };
  const writtenBarQ = fractionToNumber(measureQuarters(timeSig));

  const attacks = attackSamples(score);
  if (attacks.length < MIN_ATTACKS) return { rebar: false, reason: 'too little material' };
  const gridShare = attacks.filter((a) => onEighthGrid(a.start)).length / attacks.length;
  if (gridShare < MIN_GRID_SHARE) return { rebar: false, reason: 'attack grid unclear' };

  // The written bar length always competes as its own candidate — that is
  // what keeps a 5/4 tune (whose harmonic rhythm is 5-periodic and divisible
  // by nothing we would propose) out of the re-bar path.
  const candidates = [...new Set([2, 3, 4, writtenBarQ])].filter(
    (p) => Number.isInteger(p * 2) && p >= 2 && p <= 8,
  );
  const novelty = chromaNovelty(score);
  const scores = new Map(candidates.map((p) => [p, noveltyLift(novelty, p)]));
  const divides = (p: number) => Math.abs(writtenBarQ / p - Math.round(writtenBarQ / p)) < 1e-9;
  let best = candidates[0];
  for (const p of candidates) {
    const s = scores.get(p)!.lift;
    const sBest = scores.get(best)!.lift;
    if (s > sBest + 1e-9 || (Math.abs(s - sBest) <= 1e-9 && p < best)) best = p;
  }
  if (divides(best)) return { rebar: false, reason: 'written metre consistent with the music' };
  const bestConsistent = Math.max(...candidates.filter(divides).map((p) => scores.get(p)!.lift));
  const confidence = scores.get(best)!.lift / Math.max(bestConsistent, 1e-9);
  if (confidence < REBAR_MARGIN) {
    return { rebar: false, reason: 'metre evidence not decisive' };
  }

  // Group a 2-quarter cell as common time (the lead-sheet convention); its
  // downbeat phase is then re-read at the 4-quarter level.
  const barQ = best === 2 ? 4 : best;

  // PHASE. Harmonic novelty locates the bar only to within a beat, and in an
  // ANTICIPATED groove it is systematically one beat EARLY: the latin bass
  // sounds each new chord on the ponche (beat 4, ringing over the barline),
  // so chord-change novelty and bass-dwell mass both peak at the push — the
  // George's Dilemma audio audit (Aug 2026) put the record's one a beat
  // after every statistic the first rule trusted, and the true phase's
  // novelty share (62%) sat below the old ≥85% candidate gate entirely.
  // What anticipation cannot fake is the MELODY's long-note ARRIVALS:
  // phrases land ON the one or push INTO it, and a long note beginning on
  // beat 2 is not a phrase shape this music has (the audit head: 21 horn
  // arrivals pushed on 4, 8 on the one; the anticipation phase must read
  // those 8 as held notes starting on beat 2). With enough long melodic
  // notes, arrivals ALONE decide the phase over every slot — novelty joins
  // only as a small tie-break, because in an anticipated groove it is
  // actively wrong. Sparse content falls back to the dwelling-bass rule:
  // near-max novelty phases tie-broken by duration-weighted bass-attack
  // mass on the downbeat.
  const slots = Math.round(barQ * 2);
  const phaseSums = new Array<number>(slots).fill(0);
  for (let w = 0; w < novelty.length; w++) phaseSums[w % slots] += novelty[w];
  const maxSum = Math.max(...phaseSums);
  let phaseQ = 0;
  const arrivals = longArrivals(score);
  if (arrivals.length >= MIN_ARRIVALS) {
    let bestScore = -Infinity;
    for (let phi = 0; phi < slots; phi++) {
      let arr = 0;
      for (const a of arrivals) {
        const delta = (((a.start - phi / 2) % barQ) + barQ) % barQ;
        arr += arrivalWeight(delta, barQ);
      }
      const s =
        arr / arrivals.length +
        ARRIVAL_NOVELTY_TIEBREAK * (phaseSums[phi] / Math.max(maxSum, 1e-9));
      if (s > bestScore + 1e-9) {
        bestScore = s;
        phaseQ = phi / 2;
      }
    }
  } else {
    const bass = attacks.filter((a) => a.minMidi < 50);
    const anchorSample = bass.length >= 8 ? bass : attacks;
    const downbeatMass = (phi: number): number => {
      let mass = 0;
      for (const a of anchorSample) {
        const off = (((a.start - phi / 2) % barQ) + barQ) % barQ;
        if (Math.min(off, barQ - off) < EIGHTH_EPS) mass += a.durQ;
      }
      return mass;
    };
    let bestMass = -1;
    let bestSum = -1;
    for (let phi = 0; phi < slots; phi++) {
      if (phaseSums[phi] < 0.85 * maxSum) continue;
      const mass = downbeatMass(phi);
      if (
        mass > bestMass + 1e-9 ||
        (Math.abs(mass - bestMass) <= 1e-9 && phaseSums[phi] > bestSum)
      ) {
        bestMass = mass;
        bestSum = phaseSums[phi];
        phaseQ = phi / 2;
      }
    }
  }
  return { rebar: true, barQ, phaseQ, confidence };
}

// ---------------------------------------------------------------------------
// Respelling
// ---------------------------------------------------------------------------

/** Respell every notehead through the app's key-preference speller. Sounding
 *  pitches never change; F♭/C♭/E𝄫 become the names a reader in this key
 *  expects. Returns the new score and how many heads changed. */
export function respellToKey(score: Score, key: KeySignature): { score: Score; changed: number } {
  const context: KeyContext = { tonic: key.tonic, mode: key.mode };
  let changed = 0;
  const respellItems = (items: VoiceItem[]): VoiceItem[] =>
    items.map((item) => {
      if (item.kind === 'tuplet') return { ...item, items: respellItems(item.items) };
      if (item.kind !== 'note') return item;
      let touched = false;
      const notes = item.notes.map((head) => {
        const pitch = spellMidi(midiOf(head.pitch), { key: context });
        if (
          pitch.step === head.pitch.step &&
          pitch.alter === head.pitch.alter &&
          pitch.octave === head.pitch.octave
        ) {
          return head;
        }
        touched = true;
        changed++;
        return { ...head, pitch };
      });
      return touched ? { ...item, notes } : item;
    });
  const parts = score.parts.map((part) => ({
    ...part,
    measures: part.measures.map((pm) => ({
      ...pm,
      voices: pm.voices.map((voice) => ({ ...voice, items: respellItems(voice.items) })),
    })),
  }));
  return { score: { ...score, parts }, changed };
}

// ---------------------------------------------------------------------------
// Flatten → (quantize) → pour
// ---------------------------------------------------------------------------

interface FlatHead {
  pitch: SpelledPitch;
  /** Absolute sounding end, tie chains merged. */
  end: Fraction;
}

interface FlatAttack {
  /** The chain's first event id — kept on the attack's first written piece so
   *  unsplit notes keep their identity (selection, spanner endpoints). */
  id: string;
  start: Fraction;
  heads: FlatHead[];
  /** The first event: metadata carrier (lyrics, dynamics, articulations…). */
  source: NoteEvent;
  staff: number;
}

/** One part's voices as absolute-time attacks per voice lane (lane = voice
 *  index — the model's cross-bar voice identity), tie chains merged. */
function flattenLanes(part: Part, spans: { start: Fraction }[]): FlatAttack[][] {
  const lanes: FlatAttack[][] = [];
  const chains: Map<number, { head: FlatHead }>[] = [];
  part.measures.forEach((pm, mi) => {
    const barStart = spans[mi]?.start ?? frac(0);
    pm.voices.forEach((voice, vi) => {
      const lane = (lanes[vi] ??= []);
      const open = (chains[vi] ??= new Map());
      for (const timed of voiceTiming(voice)) {
        const ev = timed.event;
        if (ev.kind !== 'note') continue;
        const absStart = addF(barStart, timed.start);
        const absEnd = addF(absStart, timed.duration);
        let attack: FlatAttack | null = null;
        for (const head of ev.notes) {
          const midi = midiOf(head.pitch);
          const continues = head.tie === 'stop' || head.tie === 'both';
          const chain = continues ? open.get(midi) : undefined;
          if (chain && equalsF(chain.head.end, absStart)) {
            chain.head.end = absEnd;
            if (head.tie !== 'both') open.delete(midi);
            continue;
          }
          if (!attack) {
            attack = {
              id: ev.id,
              start: absStart,
              heads: [],
              source: ev,
              staff: ev.staff ?? voice.staff ?? 0,
            };
            lane.push(attack);
          }
          const flat: FlatHead = { pitch: head.pitch, end: absEnd };
          attack.heads.push(flat);
          if (head.tie === 'start' || head.tie === 'both') open.set(midi, { head: flat });
        }
      }
    });
  });
  return lanes;
}

/** Snap attack starts and head ends to the grid (mutates the lanes). Returns
 *  how many attacks moved. Collisions merge into the earlier attack. */
function quantizeLanes(lanes: FlatAttack[][], grid: Fraction): number {
  const g = fractionToNumber(grid);
  const snap = (x: Fraction): Fraction => mulF(grid, frac(Math.round(fractionToNumber(x) / g)));
  let moved = 0;
  lanes.forEach((lane, li) => {
    for (const attack of lane) {
      let touched = false;
      const start = snap(attack.start);
      if (!equalsF(start, attack.start)) {
        attack.start = start;
        touched = true;
      }
      for (const head of attack.heads) {
        let end = snap(head.end);
        if (compareF(end, addF(attack.start, grid)) < 0) end = addF(attack.start, grid);
        if (!equalsF(end, head.end)) {
          head.end = end;
          touched = true;
        }
      }
      if (touched) moved++;
    }
    // Snapping can land two attacks on one onset; the survivor keeps both
    // attacks' heads (longest end per pitch class wins).
    const merged: FlatAttack[] = [];
    for (const attack of lane.sort((a, b) => compareF(a.start, b.start))) {
      const prev = merged[merged.length - 1];
      if (prev && equalsF(prev.start, attack.start)) {
        for (const head of attack.heads) {
          const twin = prev.heads.find((h) => midiOf(h.pitch) === midiOf(head.pitch));
          if (!twin) prev.heads.push(head);
          else if (compareF(head.end, twin.end) > 0) twin.end = head.end;
        }
        moved++;
        continue;
      }
      merged.push(attack);
    }
    lanes[li] = merged;
  });
  return moved;
}

/** Pour one part's flattened lanes into the given bar spans. The inverse of
 *  flattenLanes up to notation: sounding music is preserved exactly (pinned
 *  by test); what changes is where barlines cut it. */
function pourPart(
  part: Part,
  lanes: FlatAttack[][],
  bars: { start: Fraction; end: Fraction }[],
): Part {
  const emitted = new Map<string, number>(); // attack id → continuation count
  const measures = bars.map((bar, bi) => {
    const voices: Voice[] = [];
    lanes.forEach((lane, li) => {
      const inBar = lane.filter(
        (a) =>
          compareF(a.start, bar.end) < 0 && a.heads.some((h) => compareF(h.end, bar.start) > 0),
      );
      if (inBar.length === 0) return;

      // Cut the bar at every attack and release, then read each segment's
      // sounding heads — ties fall out of whether a head continues across a
      // cut. Rests are the segments nothing sounds through.
      const cuts: Fraction[] = [bar.start, bar.end];
      for (const a of inBar) {
        if (compareF(a.start, bar.start) > 0 && compareF(a.start, bar.end) < 0) {
          cuts.push(a.start);
        }
        for (const h of a.heads) {
          if (compareF(h.end, bar.start) > 0 && compareF(h.end, bar.end) < 0) cuts.push(h.end);
        }
      }
      cuts.sort(compareF);
      const unique = cuts.filter((c, i) => i === 0 || !equalsF(c, cuts[i - 1]));

      const slots: WrittenSlot[] = [];
      const staffBySlotId = new Map<string, number>();
      let restRun: Fraction | null = null;
      let restRunStart: Fraction | null = null;
      const flushRest = () => {
        if (restRun === null || restRunStart === null) return;
        slots.push({
          quarters: restRun,
          idBase: `r:${part.id}:${li}:${bi}:${restRunStart.num}-${restRunStart.den}`,
        });
        restRun = null;
        restRunStart = null;
      };
      for (let i = 0; i + 1 < unique.length; i++) {
        const a = unique[i];
        const b = unique[i + 1];
        const width = subF(b, a);
        const sounding = inBar.filter(
          (at) => compareF(at.start, a) <= 0 && at.heads.some((h) => compareF(h.end, b) >= 0),
        );
        const heads: NoteHead[] = [];
        for (const at of sounding) {
          for (const h of at.heads) {
            if (compareF(h.end, b) < 0) continue;
            const stop = compareF(at.start, a) < 0;
            const start = compareF(h.end, b) > 0;
            heads.push({
              pitch: h.pitch,
              ...(stop && start
                ? { tie: 'both' as const }
                : stop
                  ? { tie: 'stop' as const }
                  : start
                    ? { tie: 'start' as const }
                    : {}),
            });
          }
        }
        if (heads.length === 0) {
          restRun = restRun === null ? width : addF(restRun, width);
          restRunStart ??= a;
          continue;
        }
        flushRest();
        const leader =
          sounding.find((at) => equalsF(at.start, a)) ??
          sounding.reduce((x, y) => (compareF(x.start, y.start) >= 0 ? x : y));
        const count = emitted.get(leader.id) ?? 0;
        emitted.set(leader.id, count + 1);
        const idBase = count === 0 ? leader.id : `${leader.id}@${count + 1}`;
        staffBySlotId.set(idBase, leader.staff);
        slots.push({
          quarters: width,
          idBase,
          noteheads: heads,
          ...(count === 0 ? { source: leader.source } : {}),
        });
      }
      flushRest();

      const items = slotsToItems(slots);
      // The lane's home staff in this bar is where most of its attacks live;
      // per-event overrides survive as event.staff.
      const votes = new Map<number, number>();
      for (const at of inBar) votes.set(at.staff, (votes.get(at.staff) ?? 0) + 1);
      const homeStaff = [...votes.entries()].sort((x, y) => y[1] - x[1])[0]?.[0] ?? 0;
      const fixStaff = (list: VoiceItem[]): void => {
        for (const item of list) {
          if (item.kind === 'tuplet') {
            fixStaff(item.items);
            continue;
          }
          const slotId = item.id.replace(/~\d+$/, '');
          const staff = staffBySlotId.get(slotId);
          if (item.kind === 'rest' || staff === undefined || staff === homeStaff) {
            delete item.staff;
          } else {
            item.staff = staff;
          }
        }
      };
      fixStaff(items);
      voices.push({
        id: uid('v'),
        ...(homeStaff > 0 ? { staff: homeStaff } : {}),
        items,
      });
    });
    return { id: uid('pm'), voices };
  });
  return { ...part, measures };
}

/** Re-anchor the harmony lane onto new bar spans by absolute time. A chord
 *  still sounding when a NEW symbol arrives mid-bar is restated at that bar's
 *  start (the chart model writes bars front-to-back); a bar that changes
 *  nothing stays empty and inherits, exactly as iReal charts do. */
function pourHarmony(
  form: FormMeasure[],
  oldSpans: { start: Fraction; end: Fraction }[],
  bars: { start: Fraction; end: Fraction }[],
  barDenominators: number[],
): HarmonySymbol[][] {
  let timeSig: [number, number] = form[0]?.timeSig ?? [4, 4];
  const events: { start: Fraction; chord: HarmonySymbol }[] = [];
  form.forEach((m, mi) => {
    timeSig = m.timeSig ?? timeSig;
    const beatQ = frac(4, timeSig[1]);
    let cursor = oldSpans[mi].start;
    for (const chord of m.harmony) {
      events.push({ start: cursor, chord });
      cursor = addF(cursor, mulF(frac(Math.round(chord.beats * 96), 96), beatQ));
    }
  });
  return bars.map((bar, bi) => {
    const inside = events.filter(
      (e) => compareF(e.start, bar.start) >= 0 && compareF(e.start, bar.end) < 0,
    );
    const before = events.filter((e) => compareF(e.start, bar.start) < 0);
    const active = before[before.length - 1];
    const anchors =
      inside.length > 0 && !equalsF(inside[0].start, bar.start) && active
        ? [{ start: bar.start, chord: active.chord }, ...inside]
        : inside;
    // Chord beats are DENOMINATOR units (chart convention), not quarters.
    const beatsPerQuarter = (barDenominators[bi] ?? 4) / 4;
    return anchors.map((a, i) => {
      const next = i + 1 < anchors.length ? anchors[i + 1].start : bar.end;
      return {
        ...a.chord,
        id: uid('c'),
        beats: fractionToNumber(subF(next, a.start)) * beatsPerQuarter,
      };
    });
  });
}

// ---------------------------------------------------------------------------
// Presentation — clefs, staff order, part order
// ---------------------------------------------------------------------------
//
// A transcription's CONTENT arrives right where its presentation is
// conventionally wrong (the muscriptor piano: both staves exported as bass
// clef with the bass LINE on top, and the part order scrambled around the
// skipped drum kit). Each fix below is register- or role-driven, so a
// correctly presented score is a fixed point:
//
//   • CLEF — decided by LEDGER-LINE COST on the WRITTEN register (concert +
//     the part's transposition; clefs describe the written view), never by a
//     mean-vs-middle-C split: the muscriptor right hand averages 60.8 —
//     dead-centre — while its cost numbers are unambiguous in context. A
//     multi-staff part is a grand staff, so its outer staves take the
//     conventional treble-over-bass unless the other clef is CLEARLY cheaper
//     (which keeps a genuinely two-bass-clef low piano texture, and an SA
//     choral pair's double treble, exactly as written); a lone staff re-clefs
//     only on a decisive win over a genuinely bad fit, so a borderline staff
//     never flaps. Alto/tenor/treble8vb/percussion/tab staves are authored
//     choices and are never touched, and a transposing part never takes a
//     bass clef — the written view exists to sit on treble.
//   • STAFF ORDER — a multi-staff part draws high-register staves on top (the
//     grand staff: right hand over left). Voices reference staves by index,
//     so a reorder remaps `voice.staff` and per-event overrides. Near-equal
//     registers keep their authored order.
//   • VOICE RE-HOMING — the model has no hands, so its note-to-staff filing
//     drifts bar by bar: the muscriptor intro writes the solo bass vamp on
//     the RH staff over an EMPTY LH staff, which would draw ten ledger lines
//     under the treble clef the pass just chose. A voice-bar whose EVERY
//     note sits inside the other staff's own span moves there, whole —
//     per-bar and all-or-nothing, so a genuine two-hand texture (a voice
//     straddling the break) is never split by register.
//   • PART ORDER — melody above accompaniment, the score-layout convention:
//     lead, then unroled/other melodic parts, then comp, then bass. Stable
//     within a rank, so a score with no roles never moves.

/** MIDI spans the staves cover without ledger lines: E4..F5 / G2..A3. */
const TREBLE_SPAN: [number, number] = [64, 77];
const BASS_SPAN: [number, number] = [43, 57];
/** Grand-staff outer staves keep the conventional clef unless the other is
 *  clearly cheaper — measured on the real file: RH treble 5.6 vs bass 6.0
 *  (convention must win), Rachmaninoff-low hands would be ~12 vs ~2 (the
 *  written double bass clef must survive). */
const GRAND_STAFF_TOLERANCE = 1.5;
/** A lone staff flips only when the other clef is a DECISIVE win (a bass
 *  part exported on treble runs ~15 vs ~1; a viola-register line at 4.5 vs
 *  2.5 is a judgement call and holds still)… */
const SINGLE_RECLEF_RATIO = 0.4;
/** …over a fit bad enough to matter (mean semitones outside the staff). */
const SINGLE_RECLEF_MIN_COST = 4;
/** Staves whose mean registers sit within this never reorder. */
const STAFF_ORDER_MARGIN = 3;

/** Mean semitones-outside-the-staff per note — the ledger-line bill. */
function ledgerCost(midis: number[], span: [number, number]): number {
  if (midis.length === 0) return 0;
  return midis.reduce((sum, m) => sum + Math.max(0, span[0] - m, m - span[1]), 0) / midis.length;
}

interface PresentationReport {
  /** Staves re-clefed to fit their register. */
  clefs: { part: string; from: 'treble' | 'bass'; to: 'treble' | 'bass' }[];
  /** Parts whose staves were reordered high-register-first. */
  stavesReordered: string[];
  /** Voice-bars moved to the staff whose register they sit in. */
  rehomedVoiceBars: number;
  /** Parts rearranged melody-above-accompaniment. */
  partsReordered: boolean;
}

/** Written MIDIs per staff index (clefs describe the written view). */
function staffWrittenMidis(part: Part): number[][] {
  const shift = part.transposition?.semitones ?? 0;
  const midis: number[][] = part.staves.map(() => []);
  const scan = (items: VoiceItem[], home: number): void => {
    for (const item of items) {
      if (item.kind === 'tuplet') {
        scan(item.items, home);
        continue;
      }
      const slot = midis[item.staff ?? home];
      if (!slot) continue;
      for (const head of item.notes) slot.push(midiOf(head.pitch) + shift);
    }
  };
  for (const pm of part.measures) {
    for (const voice of pm.voices) scan(voice.items, voice.staff ?? 0);
  }
  return midis;
}

const ROLE_RANK: Record<PartRole, number> = { lead: 0, other: 1, comp: 2, bass: 3 };

function fixPresentation(score: Score): { score: Score; report: PresentationReport | null } {
  const report: PresentationReport = {
    clefs: [],
    stavesReordered: [],
    rehomedVoiceBars: 0,
    partsReordered: false,
  };

  let parts = score.parts.map((part) => {
    // Staff-specific performance instructions are an authored staff choice;
    // automatic re-homing could put notes outside their instructed staff.
    if (part.measures.some((measure) => measure.playback?.some((rule) => rule.staff !== undefined))) return part;
    // Only plain treble/bass standard staves are ours to touch — a
    // notation+tab pair, a C clef or the guitar's treble8vb are authored
    // choices; one such staff parks the whole part.
    const eligible = part.staves.every(
      (s) =>
        (s.staffType ?? 'standard') === 'standard' && (s.clef === 'treble' || s.clef === 'bass'),
    );
    if (!eligible) return part;
    const midis = staffWrittenMidis(part);
    let next: Part = part;

    // Staff order first (high register on top; needs every staff occupied to
    // rank), then re-homing, then clefs on the cleaned distribution.
    if (part.staves.length >= 2 && midis.every((m) => m.length > 0)) {
      const means = midis.map((m) => m.reduce((s, x) => s + x, 0) / m.length);
      const order = part.staves.map((_, si) => si);
      const inverted = order.some((si, k) =>
        order.slice(k + 1).some((sj) => means[sj] - means[si] > STAFF_ORDER_MARGIN),
      );
      if (inverted) {
        const desired = order.slice().sort((a, b) => means[b] - means[a] || a - b);
        const newIndexOf = new Map(desired.map((oldIdx, newIdx) => [oldIdx, newIdx]));
        const remapItems = (items: VoiceItem[]): VoiceItem[] =>
          items.map((item) => {
            if (item.kind === 'tuplet') return { ...item, items: remapItems(item.items) };
            if (item.staff === undefined) return item;
            const mapped = newIndexOf.get(item.staff) ?? item.staff;
            return mapped === item.staff ? item : { ...item, staff: mapped };
          });
        next = {
          ...next,
          staves: desired.map((oldIdx) => part.staves[oldIdx]),
          measures: next.measures.map((pm) => ({
            ...pm,
            voices: pm.voices.map((voice) => {
              const mapped = newIndexOf.get(voice.staff ?? 0) ?? voice.staff ?? 0;
              const { staff: _oldStaff, ...rest } = voice;
              return {
                ...rest,
                items: remapItems(voice.items),
                ...(mapped > 0 ? { staff: mapped } : {}),
              };
            }),
          })),
        };
        report.stavesReordered.push(part.name);
      }
    }

    // Re-home misfiled voice-bars (two-staff parts): all notes inside the
    // bottom staff's own span → the bottom staff, all inside the top's → the
    // top. Whole voice-bars only — a straddling voice is a texture, not a
    // filing error.
    if (next.staves.length === 2) {
      const shift = next.transposition?.semitones ?? 0;
      let rehomed = 0;
      const measures = next.measures.map((pm) => {
        let touched = false;
        const voices = pm.voices.map((voice) => {
          const notes: number[] = [];
          const collect = (items: VoiceItem[]): void => {
            for (const item of items) {
              if (item.kind === 'tuplet') {
                collect(item.items);
                continue;
              }
              for (const head of item.notes) notes.push(midiOf(head.pitch) + shift);
            }
          };
          collect(voice.items);
          if (notes.length === 0) return voice;
          const home = voice.staff ?? 0;
          const lo = Math.min(...notes);
          const hi = Math.max(...notes);
          const want = hi <= BASS_SPAN[1] ? 1 : lo >= TREBLE_SPAN[0] ? 0 : home;
          if (want === home) return voice;
          // The whole voice-bar sits in one register: one home, no strays.
          const strip = (items: VoiceItem[]): VoiceItem[] =>
            items.map((item) => {
              if (item.kind === 'tuplet') return { ...item, items: strip(item.items) };
              if (item.staff === undefined) return item;
              const { staff: _override, ...rest } = item;
              return rest;
            });
          touched = true;
          rehomed++;
          const { staff: _home, ...rest } = voice;
          return {
            ...rest,
            items: strip(voice.items),
            ...(want > 0 ? { staff: want } : {}),
          };
        });
        return touched ? { ...pm, voices } : pm;
      });
      if (rehomed > 0) {
        next = { ...next, measures };
        report.rehomedVoiceBars += rehomed;
      }
    }

    // Clefs, on the re-homed distribution. A multi-staff part is a grand
    // staff: outer staves take the conventional treble-over-bass unless the
    // other clef is clearly cheaper to read (an empty staff costs nothing,
    // so it takes its position's convention); a lone staff flips only on a
    // decisive win over a genuinely bad fit.
    const staffMidis = next === part ? midis : staffWrittenMidis(next);
    const grand = next.staves.length >= 2;
    const withClefs = next.staves.map((staff, si) => {
      const trebleCost = ledgerCost(staffMidis[si], TREBLE_SPAN);
      const bassCost = ledgerCost(staffMidis[si], BASS_SPAN);
      const current = staff.clef as 'treble' | 'bass'; // the eligibility gate above
      let want = current;
      if (grand) {
        if (si === 0) want = trebleCost <= bassCost * GRAND_STAFF_TOLERANCE ? 'treble' : 'bass';
        else if (si === next.staves.length - 1) {
          want = bassCost <= trebleCost * GRAND_STAFF_TOLERANCE ? 'bass' : 'treble';
        } else want = trebleCost <= bassCost ? 'treble' : 'bass';
      } else if (staffMidis[si].length > 0) {
        const currentCost = current === 'treble' ? trebleCost : bassCost;
        const otherCost = current === 'treble' ? bassCost : trebleCost;
        if (
          currentCost >= SINGLE_RECLEF_MIN_COST &&
          otherCost < currentCost * SINGLE_RECLEF_RATIO
        ) {
          want = current === 'treble' ? 'bass' : 'treble';
        }
      }
      // The written view of a transposing part exists to sit on treble.
      if (want === 'bass' && next.transposition) want = current;
      if (want === current) return staff;
      report.clefs.push({ part: next.name, from: current, to: want });
      return { ...staff, clef: want };
    });
    if (withClefs.some((s, si) => s !== next.staves[si])) next = { ...next, staves: withClefs };
    return next;
  });

  // Part order — stable by construction (`|| a - b`), so unroled scores and
  // already-conventional ones never move.
  const rank = (p: Part): number => ROLE_RANK[p.role ?? 'other'] ?? 1;
  const partOrder = parts.map((_, i) => i).sort((a, b) => rank(parts[a]) - rank(parts[b]) || a - b);
  if (partOrder.some((oldIdx, newIdx) => oldIdx !== newIdx)) {
    parts = partOrder.map((i) => parts[i]);
    report.partsReordered = true;
  }

  const changed =
    report.clefs.length > 0 ||
    report.stavesReordered.length > 0 ||
    report.rehomedVoiceBars > 0 ||
    report.partsReordered;
  if (!changed) return { score, report: null };
  return { score: { ...score, parts }, report };
}

// ---------------------------------------------------------------------------
// The tidy pass
// ---------------------------------------------------------------------------

export interface TidyOptions {
  /** Detect the key from content, stamp the signature, respell (default on). */
  key?: boolean;
  /** Re-group bars when the periodicity evidence is decisive (default on). */
  rebar?: boolean;
  /** Attack grid to quantize to, in quarters (default 1/4 = a 16th note);
   *  null disables. */
  quantize?: Fraction | null;
  /** Detect chord changes into an EMPTY harmony lane (default on). Authored
   *  harmony is never overwritten. */
  chords?: boolean;
  /** Propose lead/comp part roles when none are authored (default on). */
  roles?: boolean;
  /** Fix presentation: clefs by register, grand-staff order, parts ordered
   *  melody-above-accompaniment (default on). */
  presentation?: boolean;
}

export interface TidyReport {
  key?: { detected: KeySignature; previous?: KeySignature };
  rebar?: { from: [number, number]; to: [number, number]; bars: [number, number]; phaseQ: number };
  /** Chord symbols written into the previously-empty harmony lane. */
  chords?: { symbols: number };
  /** Part roles stamped (part name → role). */
  roles?: { part: string; role: string }[];
  /** Presentation fixes applied (clefs, staff order, part order). */
  presentation?: PresentationReport;
  respelledHeads: number;
  quantizedAttacks: number;
  /** Reasons parts of the pass stood down — never silent. */
  skipped: string[];
  changed: boolean;
}

/** Structure the re-pour cannot re-group faithfully. */
function structuralObstacle(score: Score): string | null {
  const last = score.form.length - 1;
  const structured = score.form.some(
    (m, i) =>
      m.open !== undefined ||
      m.ending !== undefined ||
      m.coda ||
      m.segno ||
      m.fermata ||
      m.barRepeat !== undefined ||
      m.directive !== undefined ||
      m.section !== undefined ||
      m.sectionRole !== undefined ||
      m.feel !== undefined ||
      m.staffText !== undefined ||
      (m.close !== undefined && !(i === last && m.close === 'final')),
  );
  if (structured) return 'the form carries structure (repeats, endings, sections or navigation)';
  return null;
}

/** Content the re-pour cannot re-pour faithfully. */
function contentObstacle(score: Score): string | null {
  if (score.parts.some((part) => part.measures.some((measure) => measure.playback?.length))) {
    return 'part playback instructions present (visits are tied to their written bars)';
  }
  let tuplets = false;
  let graces = false;
  const scan = (items: VoiceItem[]): void => {
    for (const item of items) {
      if (item.kind === 'tuplet') {
        tuplets = true;
        scan(item.items);
      } else if (item.grace) {
        graces = true;
      }
    }
  };
  for (const part of score.parts) {
    for (const pm of part.measures) for (const voice of pm.voices) scan(voice.items);
  }
  if (tuplets) return 'tuplet groups present (a bracket cannot split at a new barline)';
  if (graces) return 'grace notes present';
  return null;
}

/**
 * The tidy pass for AI-transcribed scores: detect the key and respell, re-bar
 * when the evidence is decisive, quantize rhythm dust. Pure — the caller
 * commits the returned score; the report says exactly what changed and what
 * stood down.
 */
export function tidyTranscription(
  score: Score,
  options: TidyOptions = {},
): { score: Score; report: TidyReport } {
  const opts = {
    key: true,
    rebar: true,
    quantize: frac(1, 4),
    chords: true,
    roles: true,
    presentation: true,
    ...options,
  };
  const report: TidyReport = {
    respelledHeads: 0,
    quantizedAttacks: 0,
    skipped: [],
    changed: false,
  };
  let out: Score = score;

  // --- barring + quantize share the flatten→pour ---
  const structure = opts.rebar ? detectBarStructure(out) : null;
  if (opts.rebar && structure && !structure.rebar && structure.reason) {
    // "consistent" is the happy no-op; anything else is worth reporting.
    if (structure.reason !== 'written metre consistent with the music') {
      report.skipped.push(`re-bar: ${structure.reason}`);
    }
  }
  const wantsPour = (structure?.rebar ?? false) || opts.quantize !== null;
  if (wantsPour) {
    const obstacle = structuralObstacle(out) ?? contentObstacle(out);
    if (obstacle) {
      report.skipped.push(`re-bar/quantise: ${obstacle}`);
    } else {
      const oldSpans = formSpans(out.form);
      const totalQ = oldSpans.length ? oldSpans[oldSpans.length - 1].end : frac(0);

      // Flatten (and quantize) first: a pour that would change nothing is
      // DISCARDED, so a plain tidy never rewrites event ids for sport.
      const partLanes = out.parts.map((part) => flattenLanes(part, oldSpans));
      let quantized = 0;
      if (opts.quantize) {
        for (const lanes of partLanes) quantized += quantizeLanes(lanes, opts.quantize);
      }
      report.quantizedAttacks = quantized;

      if (structure?.rebar || quantized > 0) {
        let bars: { start: Fraction; end: Fraction }[];
        let newForm: FormMeasure[];
        let barDens: number[];
        if (structure?.rebar && structure.barQ !== undefined) {
          const barQ = frac(Math.round(structure.barQ * 2), 2);
          const phase = frac(Math.round((structure.phaseQ ?? 0) * 2), 2);
          // A nonzero phase makes the span before the first downbeat a
          // pickup bar — but only when something SOUNDS there (a genuine
          // anacrusis). A silent lead-in is trimmed, as an engraver would:
          // a one-beat bar of rest before the music is a phase artifact,
          // not notation. (Transcribed leading silence goes with it.)
          let firstContentQ = totalQ;
          for (const lanes of partLanes) {
            for (const lane of lanes) {
              if (lane.length > 0 && compareF(lane[0].start, firstContentQ) < 0) {
                firstContentQ = lane[0].start;
              }
            }
          }
          const oldHarmonyBar = out.form.findIndex((m) => m.harmony.length > 0);
          if (oldHarmonyBar >= 0 && compareF(oldSpans[oldHarmonyBar].start, firstContentQ) < 0) {
            firstContentQ = oldSpans[oldHarmonyBar].start;
          }
          const stub = compareF(phase, frac(0)) > 0 && compareF(firstContentQ, phase) < 0;
          bars = [];
          if (stub) bars.push({ start: frac(0), end: phase });
          let cursor = phase;
          while (compareF(cursor, totalQ) < 0) {
            bars.push({ start: cursor, end: addF(cursor, barQ) });
            cursor = addF(cursor, barQ);
          }
          const timeSig: [number, number] = [structure.barQ, 4];
          newForm = bars.map((_, i) => ({
            id: uid('m'),
            harmony: [],
            ...(i === 0
              ? {
                  timeSig,
                  ...(stub ? { pickupBeats: fractionToNumber(phase) } : {}),
                }
              : {}),
          }));
          barDens = bars.map(() => 4);
          const oldSig = out.form[0]?.timeSig ?? [4, 4];
          report.rebar = {
            from: [oldSig[0], oldSig[1]],
            to: timeSig,
            bars: [out.form.length, newForm.length],
            phaseQ: fractionToNumber(phase),
          };
        } else {
          bars = oldSpans;
          newForm = out.form.map((m) => ({ ...m }));
          let den = out.form[0]?.timeSig?.[1] ?? 4;
          barDens = out.form.map((m) => {
            den = m.timeSig?.[1] ?? den;
            return den;
          });
        }
        const harmony = pourHarmony(out.form, oldSpans, bars, barDens);
        newForm = newForm.map((m, i) => ({ ...m, harmony: harmony[i] ?? [] }));
        if (out.form[0]?.keySig && !newForm[0].keySig) newForm[0].keySig = out.form[0].keySig;

        const parts = out.parts.map((part, pi) => pourPart(part, partLanes[pi], bars));

        // Tie-chain merging dissolves continuation events; spanners that
        // referenced a dissolved id are dropped (reported), never dangling.
        const ids = new Set<string>();
        const collect = (items: VoiceItem[]): void => {
          for (const item of items) {
            if (item.kind === 'tuplet') collect(item.items);
            else ids.add(item.id);
          }
        };
        for (const part of parts) {
          for (const pm of part.measures) for (const voice of pm.voices) collect(voice.items);
        }
        const spanners = (out.spanners ?? []).filter(
          (sp) => ids.has(sp.fromEventId) && ids.has(sp.toEventId),
        );
        const droppedSpanners = (out.spanners?.length ?? 0) - spanners.length;
        if (droppedSpanners > 0) {
          report.skipped.push(`${droppedSpanners} spanner(s) dropped (endpoints re-poured)`);
        }
        out = {
          ...out,
          form: newForm,
          parts,
          ...(spanners.length > 0 ? { spanners } : {}),
        };
        if (out.spanners && spanners.length === 0) delete out.spanners;
        report.changed = true;
      }
    }
  }

  // --- key + respell (independent of the pour; run on whatever we have) ---
  if (opts.key) {
    const detected = detectKey(out);
    if (detected) {
      const previous = out.form[0]?.keySig;
      report.key = { detected, ...(previous ? { previous } : {}) };
      if (!previous || previous.tonic !== detected.tonic || previous.mode !== detected.mode) {
        const form = out.form.map((m, i) => (i === 0 ? { ...m, keySig: detected } : m));
        out = { ...out, form };
        report.changed = true;
      }
      const respelled = respellToKey(out, detected);
      if (respelled.changed > 0) {
        out = respelled.score;
        report.respelledHeads = respelled.changed;
        report.changed = true;
      }
    }
  }

  // --- part roles (never second-guessing authored ones) ---
  if (opts.roles) {
    const roles = detectPartRoles(out);
    if (roles.size > 0) {
      out = {
        ...out,
        parts: out.parts.map((p) => (roles.has(p.id) ? { ...p, role: roles.get(p.id)! } : p)),
      };
      report.roles = out.parts
        .filter((p) => roles.has(p.id))
        .map((p) => ({ part: p.name, role: roles.get(p.id)! }));
      report.changed = true;
    }
  }

  // --- presentation: clefs by register, grand staff RH-on-top, part order ---
  // After roles, because part order follows them; a correctly presented
  // score is a fixed point, so this never fights an authored layout.
  if (opts.presentation) {
    const fixed = fixPresentation(out);
    if (fixed.report) {
      out = fixed.score;
      report.presentation = fixed.report;
      report.changed = true;
    }
  }

  // --- chord detection into an EMPTY harmony lane (authored chords win) ---
  if (opts.chords && !out.form.some((m) => m.harmony.length > 0)) {
    const detected = detectHarmony(out, {
      key: report.key?.detected ?? out.form[0]?.keySig ?? null,
    });
    if (detected.ok) {
      out = {
        ...out,
        form: out.form.map((m, i) => ({ ...m, harmony: detected.result.harmony[i] ?? [] })),
      };
      report.chords = { symbols: detected.result.symbols };
      report.changed = true;
    } else {
      report.skipped.push(`chords: ${detected.reason}`);
    }
  }

  return { score: out, report };
}

/** One-line summary of a tidy report, for logs and command results. */
export function describeTidyReport(report: TidyReport): string {
  const parts: string[] = [];
  if (report.rebar) {
    parts.push(
      `re-barred ${report.rebar.bars[0]}×${report.rebar.from.join('/')} → ` +
        `${report.rebar.bars[1]}×${report.rebar.to.join('/')}`,
    );
  }
  if (report.key) {
    parts.push(`key ${report.key.detected.tonic} ${report.key.detected.mode}`);
  }
  if (report.chords) parts.push(`${report.chords.symbols} chord symbols detected`);
  if (report.roles && report.roles.length > 0) {
    parts.push(report.roles.map((r) => `${r.role}: ${r.part}`).join(', '));
  }
  if (report.presentation) {
    const p = report.presentation;
    if (p.clefs.length > 0) {
      parts.push(`${p.clefs.length} clef${p.clefs.length === 1 ? '' : 's'} refit`);
    }
    if (p.stavesReordered.length > 0) {
      parts.push(`grand staff reordered (${p.stavesReordered.join(', ')})`);
    }
    if (p.rehomedVoiceBars > 0) {
      parts.push(`${p.rehomedVoiceBars} passage${p.rehomedVoiceBars === 1 ? '' : 's'} re-homed`);
    }
    if (p.partsReordered) parts.push('parts reordered');
  }
  if (report.respelledHeads > 0) parts.push(`respelled ${report.respelledHeads} notes`);
  if (report.quantizedAttacks > 0) parts.push(`quantised ${report.quantizedAttacks} attacks`);
  if (parts.length === 0) parts.push('nothing to tidy');
  const skipped = report.skipped.length > 0 ? ` (skipped: ${report.skipped.join('; ')})` : '';
  return parts.join(', ') + skipped;
}
